/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to a component showing up the provided content as a Table
 * It is fully customizable, from the content itself to the displayed columns,
 * the render, style, sorting etc.
 */

// Imports
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from "prop-types";
import * as lodash from 'lodash';
import { Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';

// Interfaces
import { SortOrder, SortType, TableCol, TableInteractionBtn, TableRow } from '../../interfaces/IUtils';
import { PublicCabinet } from '../../interfaces/ICabinets';

// Utils
import { escapeRegExp } from '../utils/Format';

export const BCTable = (props) => {

    const cabinet = (JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet);

    const columns :TableCol[] = useMemo(() => props.columns || [] as TableCol[], [props.columns]);
    const rows :TableRow[] = useMemo(() => props.data || [] as TableRow[], [props.data]);
    const [filteredRows, setFilteredRows] = useState<TableRow[]>(props.data || [] as TableRow[]);
    let tabIndex = 50; // default

    // Pagination / selection related
    const nbRowsPerPage: number = 5;
    const selection :boolean = props.onClickRow ? false : (props.selection || false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageToGo, setPageToGo] = useState(''); // page number input 
    const [firstVisibleRowIndex, setFirstVisibleRowIndex] = useState<number>(0);
    const [visibleRows, setVisibleRows] = useState<TableRow[]>([]); // rows displayed taking care of pagination
    const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<TableRow[]>([]);

    // change the page and uncheck every document
    const goToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= Math.ceil(filteredRows.length / nbRowsPerPage))
            setCurrentPage(pageNumber);
    };

    // Select all rows from the whole page
    const togglePageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            // Select the whole page
            setSelectedRows(visibleRows);
            setWholePageSelected(true);
        } else {
            // Clear the selection
            setSelectedRows([]);
            setWholePageSelected(false);
        }
    };


    // Update the selected rows array by adding or removing the row the user clicked on
    const toggleRowSelection = (selectedRow: TableRow) => {
        let tmpSelectedRows: TableRow[] = [];

        if (selectedRows.some((someRow) => someRow.id === selectedRow.id)) {
            // If row already selected, unselect it
            tmpSelectedRows = selectedRows.filter((row) => row.id !== selectedRow.id);
        } else {
            // otherwise, select it
            tmpSelectedRows = [...selectedRows, selectedRow];
        }
        setSelectedRows(tmpSelectedRows);

        // Update the whole page selected flag
        if (tmpSelectedRows.length === visibleRows.length) setWholePageSelected(true);
        else setWholePageSelected(false);
    };

    // Define the visible rows based on the current displayed page
    useEffect(() => {
        // Calculating the first index
        let startIndex = ((currentPage - 1) * nbRowsPerPage);
        if (startIndex < 0) startIndex = 0;

        // Update the visible rows based on current pagination number
        setFirstVisibleRowIndex(startIndex);
        setVisibleRows(filteredRows.slice(startIndex, startIndex + nbRowsPerPage));

        // Clear the selection since we're changing page
        setWholePageSelected(false);
        setSelectedRows([] as TableRow[]);
    }, [filteredRows, currentPage]);




    // Sort / Search related
    const [keywords, setKeywords] = useState<string>('');
    const [currentSortedCol, setCurrentSortedCol] = useState<string>(props.sortSettings?.colName || 'id');
    const [sortOrder, setSortOrder] = useState<SortOrder>(props.sortSettings?.order || SortOrder.desc);

    /**
     * Switch the sort to the column that has been clicked on and switch the sort order if it was the same as before
     */
    const sortBy = (column: TableCol) => {
        // Since it's the same column that was already sorted, just switch the order
        if (column.name === currentSortedCol)
            setSortOrder((sortOrder === SortOrder.asc) ? SortOrder.desc : SortOrder.asc);
        else {
            // otherwise change the sorted field and set it to asc
            setCurrentSortedCol(column.name);
            setSortOrder(SortOrder.asc);
        }
    }


    /**
     * Get rows matching a specific keyword (used by the searchbar)
     * @since 2024-04-09
     * @author Mickaël "Naxe67" APPEL
     * @returns {TableRow[]}
     */
    const getRowsMatchingKeywords = useCallback(() :TableRow[] => {
        // Create a RegExp from the user query
        const search = new RegExp(escapeRegExp(keywords), 'mi');

        // Browse each row in order to check if value for each columns (that has been set up to be use for research) matchs the keyword
        return rows.filter((row) => {
            
            // Browse each column to check if we need to perform the research on this column and if so, do it
            return columns.some(column => {
                // Column not used for research
                if (!column.usedForResearch) return false;
                
                // Column used for research so process it
                return search.test(row[column.name]);
            });
        });
    }, [rows, columns, keywords]);


    // Sort rows when data/keywords or the sort column/order changes
    useEffect(() => {

        // Perform the search on keyword if needed
        const tmpMatchingRows :TableRow[] = (props.searchbar && keywords.length > 0) ? getRowsMatchingKeywords() : rows;

        // Prepare the sorting and handle errors
        const columnToSortBy :TableCol | undefined = columns.find(column => column.name === currentSortedCol);
        if (!columnToSortBy || !columnToSortBy.sort) {
            setFilteredRows(tmpMatchingRows);
            return;
        } 
        
        // Perform the sorting
        switch (true) {

            // Text field - Order alphanumeric value (case insensitive)
            case (columnToSortBy.sort === SortType.Alphanumeric):
                setFilteredRows(lodash.orderBy(tmpMatchingRows, [(row :TableRow) => (row[currentSortedCol] !== undefined) ? row[currentSortedCol].toLowerCase() : ''], [sortOrder]));
                break;

            // Number field
            case (columnToSortBy.sort === SortType.Number):
                setFilteredRows(lodash.orderBy(tmpMatchingRows, [(row :TableRow) => (row[currentSortedCol] !== null ? row[currentSortedCol] : -1)], [sortOrder])); // filter by text (case insensitive)
                break;

            // Date field
            case (columnToSortBy.sort === SortType.Date):
                setFilteredRows(lodash.orderBy(tmpMatchingRows, [(row :TableRow) => new Date(row[currentSortedCol])], [sortOrder]));
                break;            

            // Custom pre-processing
            case (typeof columnToSortBy.sort === 'function'):
                setFilteredRows(lodash.orderBy(tmpMatchingRows, [(row :TableRow) => ((columnToSortBy.sort as Function)(row[currentSortedCol]))], [sortOrder]));
                break;
            
            // Otherwise, return the whole matching rows
            default:
                setFilteredRows(tmpMatchingRows);
                break;
        }

        setCurrentPage(1);
    }, [rows, keywords, currentSortedCol, sortOrder, columns, props.searchbar, getRowsMatchingKeywords]);


    // UI Functions

    // Get the sort icon of a column (upper or lower arrow for asc/desc order)
    const getSortIconForCol = (colName: string): React.ReactNode => {
        if (colName === currentSortedCol)
            return (<FontAwesomeIcon className="float-right mr-2" icon={sortOrder === SortOrder.asc ? faSortUp : faSortDown} />);
        else
            return (<></>);
    }

    // JSX Template
    return (
        <div className='w-[100%]'>
            <div className='overflow-x-auto flex-grow'>

                {/* Toolbar */}
                {(props.searchbar || props.buttons?.length > 0) && <div className={`flex flex-row w-full place-items-center ${props.toolbarClassName || ''}`}>
                    {/* Searchbar */}
                    {props.searchbar && <div className='w-[95.5%]'><input tabIndex={tabIndex++} type="text" placeholder="Rechercher" value={keywords} onChange={(e) => setKeywords(e.target.value)}
                        className="py-3 pl-10 pr-3 border border-gray-300 w-1/2 h-12 rounded-lg text-gray-500" />
                    <FontAwesomeIcon className="relative w-6 h-6 right-1/2 mx-2" icon={faMagnifyingGlass} /></div>}
                    <div className="grow"></div>

                    {/* Interaction Buttons */}
                    {props.buttons?.length > 0 && <div className='grow text-end p-1 pb-0'>
                        {props.buttons.map((interactiveBtn :TableInteractionBtn) => {
                            const clickFunction = () => {
                                // Triggers the action if no conditions
                                if (interactiveBtn.withoutSelection) interactiveBtn.action(selectedRows);
                                else {
                                    // Triggers the action if a selection was made
                                    if (selectedRows.length > 0) interactiveBtn.action(selectedRows);
                                }
                            }
                            return <FontAwesomeIcon key={`interactivebtn-${interactiveBtn.name}`} title={interactiveBtn.name} icon={interactiveBtn.icon} tabIndex={tabIndex++} style={{borderColor: cabinet.color + '92', color: cabinet.color}} className={`h-7 w-7 cursor-pointer border-1 bc-btn object-contain p-1 focus:outline-none ${!interactiveBtn.withoutSelection && selectedRows.length === 0 ? 'disabled' : ''}`} onClick={clickFunction} />
                        })}
                    </div>}

                </div>}

                {/* Table itself */}
                <table id={props.id ?? 'bctable'} className={`border w-full ${props.className || ''}`} >

                    {/* Header row */}
                    <thead>
                        <tr className="bg-gray-200">
                            {/* Checkboxes */}
                            {selection && <th tabIndex={tabIndex++} className='p-2 w-8 text-center'><input type="checkbox" className='cursor-pointer' checked={wholePageSelected} onChange={togglePageSelection} /></th>}

                            {/* Browse each column one by one */}
                            {columns.map((column) => {
                                tabIndex++;
                                return (
                                    <th tabIndex={column.tabIndex || tabIndex} key={props.customKey + '-column-' + column.name + '-sort-' + currentSortedCol + '-' + sortOrder} className={`p-2 ${!column.show ? 'hidden' : ''} ${column.sort ? 'cursor-pointer' : ''} ${column.className || ''}`} onClick={() => column.sort ? sortBy(column) : ''}>
                                        {column.name} {/* Column name */}
                                        {column.hint && <Tooltip arrow={true} placement='top' classes={{tooltip:'bc-tooltip'}} title={column.hint}><svg className='inline' height={32} width={32} viewBox="0 0 281.25 281.25" id="svg2" version="1.1" fill="#000000"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" stroke="#599ef7" strokeWidth="4.5"> <defs id="defs4"></defs> <g id="layer1" transform="translate(6402.3564,-3399.5356)"> <path d="m -6261.7314,3438.3744 c -56.159,-1e-4 -101.7865,45.6274 -101.7865,101.7865 0,56.1591 45.6274,101.7865 101.7865,101.7865 56.1591,0 101.7865,-45.6274 101.7865,-101.7865 0,-56.1591 -45.6275,-101.7865 -101.7865,-101.7865 z m 0,9.375 c 51.0924,0 92.4115,41.3191 92.4115,92.4115 0,51.0925 -41.319,92.4115 -92.4115,92.4115 -51.0924,0 -92.4115,-41.319 -92.4115,-92.4115 0,-51.0925 41.3191,-92.4115 92.4115,-92.4115 z m 0.3717,26.8048 c -1.2339,-0.015 -2.47,0.023 -3.7042,0.1172 -9.8736,0.754 -19.3268,5.0163 -26.4295,11.9165 -7.1027,6.9002 -11.6353,16.2263 -12.6745,26.0742 a 4.6875,4.6875 0 0 0 4.1675,5.1544 4.6875,4.6875 0 0 0 5.1544,-4.1693 c 0.8026,-7.6061 4.3998,-15.0042 9.8858,-20.3338 5.4858,-5.3294 12.9843,-8.7121 20.6104,-9.2945 7.6262,-0.5823 15.5526,1.6225 21.784,6.0572 6.2314,4.4347 10.9094,11.2001 12.8577,18.5962 2.5369,9.6304 0.347,20.6358 -5.6817,28.5626 -5.6107,7.3767 -14.6803,12.254 -23.9374,12.9986 a 4.6875,4.6875 0 0 0 -2.4042,-0.6665 4.6875,4.6875 0 0 0 -4.6875,4.6875 v 28.125 a 4.6875,4.6875 0 0 0 4.6875,4.6875 4.6875,4.6875 0 0 0 4.6875,-4.6875 v -22.9541 c 11.4217,-1.3216 22.12,-7.3184 29.1156,-16.5161 7.8545,-10.3274 10.589,-24.0778 7.2839,-36.6247 -2.5225,-9.5759 -8.4188,-18.106 -16.4868,-23.8477 -7.0595,-5.024 -15.591,-7.7776 -24.2285,-7.8827 z m 0.022,123.327 a 4.6875,4.6875 0 0 0 -4.6875,4.6875 v 10.7117 a 4.6875,4.6875 0 0 0 4.6875,4.6875 4.6875,4.6875 0 0 0 4.6875,-4.6875 v -10.7117 a 4.6875,4.6875 0 0 0 -4.6875,-4.6875 z" id="circle1324" fill="#599ef7"></path> </g> </g><g id="SVGRepo_iconCarrier"> <defs id="defs4"></defs> <g id="layer1" transform="translate(6402.3564,-3399.5356)"> <path d="m -6261.7314,3438.3744 c -56.159,-1e-4 -101.7865,45.6274 -101.7865,101.7865 0,56.1591 45.6274,101.7865 101.7865,101.7865 56.1591,0 101.7865,-45.6274 101.7865,-101.7865 0,-56.1591 -45.6275,-101.7865 -101.7865,-101.7865 z m 0,9.375 c 51.0924,0 92.4115,41.3191 92.4115,92.4115 0,51.0925 -41.319,92.4115 -92.4115,92.4115 -51.0924,0 -92.4115,-41.319 -92.4115,-92.4115 0,-51.0925 41.3191,-92.4115 92.4115,-92.4115 z m 0.3717,26.8048 c -1.2339,-0.015 -2.47,0.023 -3.7042,0.1172 -9.8736,0.754 -19.3268,5.0163 -26.4295,11.9165 -7.1027,6.9002 -11.6353,16.2263 -12.6745,26.0742 a 4.6875,4.6875 0 0 0 4.1675,5.1544 4.6875,4.6875 0 0 0 5.1544,-4.1693 c 0.8026,-7.6061 4.3998,-15.0042 9.8858,-20.3338 5.4858,-5.3294 12.9843,-8.7121 20.6104,-9.2945 7.6262,-0.5823 15.5526,1.6225 21.784,6.0572 6.2314,4.4347 10.9094,11.2001 12.8577,18.5962 2.5369,9.6304 0.347,20.6358 -5.6817,28.5626 -5.6107,7.3767 -14.6803,12.254 -23.9374,12.9986 a 4.6875,4.6875 0 0 0 -2.4042,-0.6665 4.6875,4.6875 0 0 0 -4.6875,4.6875 v 28.125 a 4.6875,4.6875 0 0 0 4.6875,4.6875 4.6875,4.6875 0 0 0 4.6875,-4.6875 v -22.9541 c 11.4217,-1.3216 22.12,-7.3184 29.1156,-16.5161 7.8545,-10.3274 10.589,-24.0778 7.2839,-36.6247 -2.5225,-9.5759 -8.4188,-18.106 -16.4868,-23.8477 -7.0595,-5.024 -15.591,-7.7776 -24.2285,-7.8827 z m 0.022,123.327 a 4.6875,4.6875 0 0 0 -4.6875,4.6875 v 10.7117 a 4.6875,4.6875 0 0 0 4.6875,4.6875 4.6875,4.6875 0 0 0 4.6875,-4.6875 v -10.7117 a 4.6875,4.6875 0 0 0 -4.6875,-4.6875 z" id="circle1324" fill="#599ef7"></path> </g> </g></svg></Tooltip>} {/* Column hint if provided*/}
                                        {getSortIconForCol(column.name)} {/* Column sort icon if sorted by this column */}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>



                    {/* Content */}
                    <tbody>
                        {/* 1 tr element by row with checkbox / selection if defined */}
                        {visibleRows.map((row, index) => (
                            <tr key={props.customKey + '-row' + index + '-' + row.id} className={`cursor-default border-b border-gray-300 ${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'} ${props.rowClassName || ''}`}
                                tabIndex={tabIndex++} onClick={() => { if (selection) { toggleRowSelection(row) } else if (props.onRowClick) props.onRowClick(row)}}>
                                <td className={`p-2 w-8 text-center ${!selection ? 'hidden' : ''}`}><input key={`${props.customKey}-select${index}-${row.id}`} type="checkbox" onChange={() => toggleRowSelection(row)} checked={selectedRows.some((checkingRow) => checkingRow.id === row.id)} /></td>
                                {
                                    /* 1 td element by column */
                                    columns.map((column) => {
                                        return (<td key={props.customKey + '-row-' + row.id + '-' + column.name} className={`p-2 whitespace-pre-wrap ${column.className || ''} ${!column.show ? 'hidden' : ''}`}>{column.formatBeforeRender ? column.formatBeforeRender(row, column.name) : row[column.name]}</td>);
                                    })
                                }
                            </tr>
                        ))}
                    </tbody>
                </table >
            </div>


            {/* Pagination */}
            <div className="flex justify-between mt-2 overflow-hidden p-1 pb-0">
                {filteredRows.length === 0 ?
                    <span className="text-xs">{props.emptyMsg || 'Aucune ligne à afficher'}</span> :
                    <span className="text-xs">{(props.paginationMsg || 'Lignes [0] à [1] sur [2]').replaceAll('[0]', firstVisibleRowIndex + 1).replaceAll('[1]', (firstVisibleRowIndex + nbRowsPerPage) > filteredRows.length ? filteredRows.length : firstVisibleRowIndex + nbRowsPerPage).replaceAll('[2]', filteredRows.length)}</span>}
                <div className="pagination">
                    {/* Previous */}
                    <button tabIndex={80} className='ml-4 py-1 px-2 border rounded bg-grey-500' onClick={() => goToPage(currentPage - 1)}>‹</button>

                    {Array.from({ length: Math.ceil(filteredRows.length / nbRowsPerPage) }, (_, index) => (
                        index + 1 === currentPage ?
                            <input tabIndex={81 + index} key={'page-input'} type="number" min={1} max={Math.ceil(filteredRows.length / nbRowsPerPage)} value={pageToGo} placeholder={`Page ${currentPage}`}
                                onChange={(e) => setPageToGo(e.target.value)}
                                onKeyDown={(e) => (e.key === 'Enter' ? goToPage(Number(pageToGo)) : '')}
                                className="ml-4 py-1 px-2 border rounded bg-grey-500 w-20 text-center no-arrow"></input>
                            :
                            <button tabIndex={81 + index} key={`pagination-${index}`} className={`ml-4 py-1 px-2 border rounded bg-grey-500 ${((index + 1 >= currentPage - 1) && (index + 1 <= currentPage + 1)) ? 'inline-block' : 'hidden'}`} onClick={() => setCurrentPage(index + 1)}>{index + 1}</button>
                    ))}

                    {/* Next */}
                    <button tabIndex={96} className='ml-4 py-1 px-2 border rounded bg-grey-500' onClick={() => goToPage(currentPage + 1)}>›</button>
                </div>
            </div>
        </div>
    );
};

BCTable.propTypes = {
    id: PropTypes.string,
    className: PropTypes.string, // style to apply on the table itself
    rowClassName: PropTypes.string, // style to apply on each row
    columns: PropTypes.arrayOf(PropTypes.object).isRequired, // table's headers / columns
    data: PropTypes.arrayOf(PropTypes.object).isRequired, // table's body / row
    customKey: PropTypes.string,

    emptyMsg: PropTypes.string, // message to display when there's no row
    paginationMsg: PropTypes.string, // pagination message
    
    onRowClick: PropTypes.func, // Function to execute when the user clicked on a row (WARNING : Not compatible with selection property)
    selection: PropTypes.bool, // Checkbox for each row + 1 global (WARNING : Not compatible with onRowClick property)
    toolbarClassName: PropTypes.string,
    buttons: PropTypes.arrayOf(PropTypes.object), // Buttons on the toolbar
    // Example [{name: 'Télécharger', icon: faDownload, action: (selectedRows :TableRow[]) => { console.log(selectedRows); }}]
    
    sortSettings: PropTypes.object, // Default sorting columns + order
    searchbar: PropTypes.bool // searchbar (looking for keyword accross columns defined to be use in this search, if none specify, it'll look accross every columns)
    
}